import { createContext, useState, ReactNode, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNextgenRouter } from '@nextgen-web-framework/all';
import { StorageService, Logger } from '@nextgen-web-framework/core';
import { sharebarApi, ApiProjections, AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { useLoader } from '../LoaderProvider/LoaderProvider';
import { LocalStorageKeys } from '../../utils/common';
import logObject from '../../../logObject';

type SharecodeContextValue = {
  sharecodeData: AdvanceShareCodeResponse | null;
  updateSharecode: (data: AdvanceShareCodeResponse | null) => void;
};

const SharecodeContext = createContext<SharecodeContextValue>({
  sharecodeData: null,
  updateSharecode: () => {
    /* do nothing */
  },
});

const logger = Logger.getInstance(logObject('SharecodeProvider.tsx'));

function SharecodeProvider(props: { children: ReactNode }) {
  const [sharecodeData, setSharecodeData] = useState<AdvanceShareCodeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const router = useNextgenRouter();
  const localstorage = StorageService.getLocalStorage();
  const sharecodeid = router.query?.get('sharecodeid');

  const handleSharecodeAttributes = async (sharecodeid: string) => {
    try {
      const sharecodeAttributes = await sharebarApi.getSharecodeDetails({
        sharecodeid: sharecodeid,
        projections: ApiProjections.ALL,
      });
      localstorage.store(LocalStorageKeys.SHARECODE_ATTRIBUTES, sharecodeAttributes);
      setSharecodeData(sharecodeAttributes as AdvanceShareCodeResponse);
    } catch (err) {
      logger.error(err);
      router.push('/');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (sharecodeid && typeof sharecodeid === 'string') {
      setLoading(true);
      handleSharecodeAttributes(sharecodeid);
    } else {
      setSharecodeData(localstorage.retrieve(LocalStorageKeys.SHARECODE_ATTRIBUTES) as AdvanceShareCodeResponse);
    }
  }, [sharecodeid]);

  useEffect(() => {
    if (loading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [loading, showLoader, hideLoader]);

  const updateSharecode = useCallback((data: AdvanceShareCodeResponse | null) => {
    setSharecodeData(data);
    if (data) {
      localstorage.store(LocalStorageKeys.SHARECODE_ATTRIBUTES, data);
    }
  }, []);
  const contextValue = useMemo(() => ({ sharecodeData, updateSharecode }), [sharecodeData, updateSharecode]);
  return (
    <SharecodeContext.Provider value={contextValue}>
      {loading ? <></> : <>{props.children}</>}
    </SharecodeContext.Provider>
  );
}

export default SharecodeProvider;

export function useSharecode() {
  const context = useContext(SharecodeContext);
  return context;
}
